import {
  Question_Correct_Count_Type,
  Question_Type,
  SaveAnswerInput,
  useSaveAnswerMutation,
} from '@generated/graphql';
import { Box, Button, Grid2, Typography } from '@mui/material';
import {
  ClassicEditor,
  Form,
  TextEditorControl,
  TextFieldControl,
  useToast,
} from '@myhrbox/myhrbox-frontend-core';
import '@myhrbox/myhrbox-frontend-core/dist/myhrbox-frontend-core.css';
import TextFormatter from 'components/TextFormatter';
import { useDialog } from 'context/dialogContext';
import { useUnit } from 'effector-react';
import usePreventCopy from 'hooks/usePreventCopy';
import useVoiceRecorded, { RecordStatus } from 'hooks/useVoiceRecorded';
import { ClipboardEvent, useContext, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  $assessmentInfo,
  $currentQuestion,
  $insertCount,
  $insertSymbols,
  $questionIndex,
  $questions,
  setInsertCount,
  setInsertSymbols,
} from 'store/assessment/model';
import { $questionTimer } from 'store/assessment/questionTimer';
import QuestionHeader from '../components/QuestionHeader';
import SkipDelayControls from '../components/SkipDelayControls';
import VoiceRecordInstruction from '../components/VoiceRecordInstruction';
import { QuestionPageContext } from '../context';
import MultiChoiceAnswer from './components/MultiChoiceAnswer';
import SingleChoiceAnswer from './components/SingleChoiceAnswer';
import VoiceRecordButton from './components/VoiceRecordButton';
import { IAnswerForm } from './types';

const SingleQuestion = () => {
  const {
    handlePreventSelectStart,
    handlePreventCopy,
    handlePreventContextMenu,
    preventCopyStyles,
  } = usePreventCopy();

  // TODO: вернуть, когда понадобится
  // const { preventPasteStyles } = usePreventPaste();

  const { t } = useTranslation();
  const [
    currentQuestion,
    questionIndex,
    assessmentInfo,
    questionTimer,
    questions,
    insertCount,
    insertSymbols,
  ] = useUnit([
    $currentQuestion,
    $questionIndex,
    $assessmentInfo,
    $questionTimer,
    $questions,
    $insertCount,
    $insertSymbols,
  ]);
  const [isSaveAnswerLoading, setIsSaveAnswerLoading] = useState(false);
  const { showDialog, closeDialog } = useDialog();
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    isDelayLoading,
    isSkipLoading,
    delayQuestionLoading,
    skipQuestionLoading,
    handleFinishQuestion,
    handleFinishTesting,
    isLastQuestion,
  } = useContext(QuestionPageContext);

  const [saveAnswer, { loading: saveAnswerLoading }] = useSaveAnswerMutation();

  const form = useForm<IAnswerForm>({
    defaultValues: {
      answer: '',
      voiceRecord: undefined,
      partId: undefined,
      parts: currentQuestion?.parts?.map(({ id: partId, content }) => ({
        id: partId,
        content,
        checked: false,
      })),
    },
  });
  const { control, handleSubmit, setValue, watch } = form;
  const voiceRecordValue = useWatch({ control, name: 'voiceRecord' });
  const voiceRecorded = useVoiceRecorded({
    onTranscriptionSuccess: (transcribedText) => {
      setValue('answer', `${watch('answer')} ${transcribedText}`, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  });

  const isMultiChoiceQuestion = currentQuestion?.type === Question_Type.MultipleChoiceQuestions;
  const questionOrder = questions[questionIndex].order + 1;

  const onConfirm = async (formData: IAnswerForm) => {
    if (currentQuestion && assessmentInfo) {
      let mp3File;

      if (formData.voiceRecord) {
        const mp3Blob = formData.voiceRecord;
        mp3File = new File([mp3Blob], 'audio.mp3', { type: 'audio/mpeg' });
      }

      const input: SaveAnswerInput = {
        audioFile: mp3File,
        answer: formData.answer,
        time: questionTimer,
        questionId: currentQuestion.id,
        trialId: assessmentInfo.id,
        metadata: {
          insertCount,
          insertSymbols,
        },
      };

      if (isMultiChoiceQuestion) {
        input.partsIds = formData.partId
          ? [formData.partId]
          : formData.parts?.filter((part) => part.checked).map((part) => part.id);
      }

      setIsSaveAnswerLoading(true);
      saveAnswer({
        variables: {
          input,
        },
      })
        .then((res) => {
          if (res.data?.saveAnswer) {
            showSuccessToast(t('successMessage'));

            if (isLastQuestion) {
              handleFinishTesting();
            } else {
              handleFinishQuestion?.()?.finally(() => {
                setIsSaveAnswerLoading(false);
              });
            }
          } else {
            showErrorToast(t('errors.common'));
          }
        })
        .catch(() => {
          setIsSaveAnswerLoading(false);
          showErrorToast(t('errors.common'));
        });
    } else {
      showErrorToast(t('errors.common'));
    }
  };

  const onSaveAnswer = (formData: IAnswerForm) => {
    const isRecordingInProgress = voiceRecorded.status === RecordStatus.Recording;

    if (isRecordingInProgress) {
      showDialog({
        title: t('assessment.question.recordingInProgress.title'),
        content: t('assessment.question.recordingInProgress.message'),
        cancelText: t('assessment.question.recordingInProgress.confirm'),
      });
    } else {
      showDialog({
        title: t('assessment.confirmWarning.title'),
        content: t('assessment.confirmWarning.description'),
        confirmText: t('confirm'),
        cancelText: t('cancel'),
        onConfirm: async () => {
          closeDialog();
          await onConfirm(formData);
        },
      });
    }
  };

  const isAudioConverting = voiceRecorded.status === RecordStatus.Converting;
  const isVoiceTranscriptionInProgress = Boolean(voiceRecordValue) && voiceRecorded.isTranscribing;

  const updateInsertParams = (clipboardData: DataTransfer) => {
    const insertedText = clipboardData.getData('text/html');
    setInsertCount(insertCount + 1);
    setInsertSymbols(insertSymbols + insertedText.length);
  };

  const onPasteAnswer = (e: ClipboardEvent<HTMLDivElement>) => {
    updateInsertParams(e.clipboardData);
  };

  const handleReady = (editor: ClassicEditor) => {
    editor.editing.view.document.on('paste', (_, data) => {
      updateInsertParams(data.dataTransfer);
    });
  };

  const handleVoiceRecordRemove = (removeVoiceRecord: () => void) => {
    showDialog({
      title: t('assessment.question.voice.removeAnswerConfirm.title'),
      content: (
        <Box>
          <Typography variant='body1'>
            {t('assessment.question.voice.removeAnswerConfirm.content')}{' '}
            {t('assessment.question.voice.removeAnswerConfirm.warning')}
          </Typography>
        </Box>
      ),
      cancelText: t('assessment.question.voice.removeAnswerConfirm.cancel'),
      confirmText: t('assessment.question.voice.removeAnswerConfirm.confirm'),
      onConfirm: async () => {
        closeDialog();
        removeVoiceRecord();
        setValue('answer', '', { shouldDirty: true, shouldValidate: false });
      },
    });
  };

  const getAnswerComponent = () => {
    const isTextAnswerRequired = t('validation.fieldRequired');

    if (currentQuestion?.type === Question_Type.Practical) {
      return (
        <TextEditorControl
          name='answer'
          onReady={handleReady}
          rules={{ required: isTextAnswerRequired }}
        />
      );
    }

    if (isMultiChoiceQuestion) {
      if (currentQuestion.correctCountType === Question_Correct_Count_Type.SingleCorrect) {
        return <SingleChoiceAnswer currentQuestion={currentQuestion} />;
      }

      return <MultiChoiceAnswer form={form} />;
    }

    return (
      <TextFieldControl
        name='answer'
        multiline
        minRows={5}
        onPaste={onPasteAnswer}
        rules={{ required: isTextAnswerRequired }}
        // TODO: вернуть, когда понадобится
        // sx={preventPasteStyles}
        // onContextMenu={(e) => e.preventDefault()}
      />
    );
  };

  const skipDelayButtonsDisabled =
    saveAnswerLoading || isSaveAnswerLoading || isAudioConverting || isVoiceTranscriptionInProgress;

  return (
    <Box>
      <Typography variant='h28' mb={1.5}>
        {t('assessment.question.title', { order: questionOrder })}
      </Typography>
      <TextFormatter
        text={currentQuestion?.text || ''}
        variant='body16rg'
        mb={3.8}
        sx={preventCopyStyles}
        onSelectCapture={handlePreventSelectStart}
        onCopy={handlePreventCopy}
        onContextMenu={handlePreventContextMenu}
      />
      <QuestionHeader title={t('assessment.question.answerTitle')} />
      <Form form={form}>
        <Box mb={2}>{getAnswerComponent()}</Box>
        <Grid2 container spacing={2}>
          {!isMultiChoiceQuestion && (
            <Grid2 size={{ xs: 12, md: 6 }}>
              <VoiceRecordButton
                voiceRecorded={voiceRecorded}
                onBeforeRemove={
                  voiceRecorded.status !== RecordStatus.Recording &&
                  !voiceRecorded.transcribingError
                    ? handleVoiceRecordRemove
                    : undefined
                }
              />
            </Grid2>
          )}
          <Grid2 size={{ xs: 12, md: isMultiChoiceQuestion ? 12 : 6 }}>
            <Button
              fullWidth
              size='large'
              disabled={
                isAudioConverting ||
                isVoiceTranscriptionInProgress ||
                skipQuestionLoading ||
                isSkipLoading ||
                delayQuestionLoading ||
                isDelayLoading
              }
              loading={saveAnswerLoading || isSaveAnswerLoading}
              variant='contained'
              onClick={handleSubmit(onSaveAnswer)}>
              {t('assessment.question.submitAnswer')}
            </Button>
          </Grid2>
        </Grid2>
      </Form>
      {!isMultiChoiceQuestion && <VoiceRecordInstruction />}
      <SkipDelayControls
        skipButtonProps={{ disabled: skipDelayButtonsDisabled }}
        delayButtonProps={{ disabled: skipDelayButtonsDisabled }}
      />
    </Box>
  );
};

export default SingleQuestion;
